import React, { useState, useEffect, useMemo } from "react";
import {
  License,
  LicenseSeat,
  CreateLicenseData,
  EditLicenseData,
} from "../../store/types";
import { LICENSE_KIND_LABELS, LicenseKind } from "../../types/licenseTypes";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { validateLicenseField } from "../../hooks/validation";
import { formatDate } from "../../hooks/dateTimeConvertion";
import {
  getLicenseKind,
  getLicenseKindLabel,
} from "../../hooks/useLicenseType";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import CustomInputNumberIncrement from "../../components/CustomInput/CustomInputNumberIncrement";
import CustomDropdown from "../../components/CustomInput/CustomDropdown";
import CustomCalendar from "../../components/CustomInput/CustomCalendar";
import { Container } from "../../components/Container/Container";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import { BodyText, DangerText, H2 } from "../../styles/Typography";
import {
  IconPlusBlack,
  IconCalendar,
  IconDocument,
  IconCompany,
  IconCalendarCheckFilled,
  IconCalendarXFilled,
  IconDocFilled,
  IconCompanyFilled,
  IconCaseFilled,
  IconCase,
  IconUserFilled,
  IconUser,
  IconArrowLeft,
} from "../../assets/icons/Icon";
import {
  Flex,
  ButtonLabel,
  GoBackButton,
  ButtonContainer,
  Spinner,
  Column,
  SmallColumn,
  Row,
  RowText,
} from "./CRUDPages.styles";

const initialFormData: CreateLicenseData = {
  kind: null,
  projectId: "",
  organizationId: 0,
  startsAt: null,
  expiresAt: null,
  maxActiveUsers: null,
  comments: "",
  count: 1,
};

const initialErrors: Record<keyof CreateLicenseData, boolean> = {
  kind: false,
  projectId: false,
  organizationId: false,
  startsAt: false,
  expiresAt: false,
  maxActiveUsers: false,
  comments: false,
  count: false,
};

const seatName = (seat?: LicenseSeat | null): string =>
  seat?.fullName || seat?.shortName || "Не добавлен";

const LicenseFormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const licenseId = searchParams.get("id");
  const { companyId } = useParams<{ companyId: string }>();
  const { userId } = useParams<{ userId: string }>();
  const { projectId } = useParams<{ projectId: string }>();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const mode = searchParams.get("mode") || "create";

  const [loading, setLoading] = useState(false);
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleAssignSeat,
    handleRemoveSeat,
    handleDeleteLicense,
    refetchData,
    toastRef,
  } = useLicensesAPI();
  const { companiesEP, companies } = useCompaniesAPI();
  const { projectsEP, projects } = useProjectsAPI();
  const { usersEP, users } = useUsersAPI();

  const [formData, setFormData] = useState<CreateLicenseData>(initialFormData);
  const [errors, setErrors] =
    useState<Record<keyof CreateLicenseData, boolean>>(initialErrors);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [addUserOpt, setAddUserOpt] = useState<{ label: string; value: any }[]>(
    []
  );
  const [isAvailableUsers, setIsAvailableUsers] = useState<boolean>(true);
  const [companiesOpt, setCompaniesOpt] = useState<
    { label: string; value: any }[]
  >([]);
  const [projectsOpt, setProjectsOpt] = useState<
    { label: string; value: any }[]
  >([]);
  const [isAddUser, setIsAddUser] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  const { data: license, isLoading } = useGetQuery(
    `${licensesEP}/${licenseId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !licenseId || !licensesEP,
    }
  );

  const { data: user } = useGetQuery(`${usersEP}/${userId}`, {
    refetchOnMountOrArgChange: true,
    skip: !usersEP || !userId,
  });

  const { data: company } = useGetQuery(
    `${companiesEP}/${user?.organizationId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !companiesEP || !user,
    }
  );

  const { data: project } = useGetQuery(`${projectsEP}/${projectId}`, {
    refetchOnMountOrArgChange: true,
    skip: !projectsEP || !projectId,
  });

  const seats: LicenseSeat[] = useMemo(
    () => (license as License | undefined)?.seats ?? [],
    [license]
  );
  const currentSeat = seats.length > 0 ? seats[0] : null;

  const isPerpetual = formData.kind === LicenseKind.Perpetual;
  const isSession = formData.kind === LicenseKind.Session;

  useEffect(() => {
    if (companies) {
      setCompaniesOpt(companies.map((c) => ({ label: c.name, value: c.id })));
    }
  }, [companies]);

  useEffect(() => {
    if (projects) {
      setProjectsOpt(projects.map((p) => ({ label: p.name, value: p.id })));
    }
  }, [projects]);

  useEffect(() => {
    if (users && license?.organizationId) {
      const filtered = users
        .filter((u) => u.organizationId === license.organizationId)
        .map((u) => ({
          label: `${u.lastName ?? ""} ${u.name ?? ""} ${u.patronymic ?? ""}`,
          value: u.id,
        }));
      setAddUserOpt(filtered);
    }
  }, [users, license?.organizationId, mode]);

  useEffect(() => {
    if (mode === "editUser" && currentSeat) setSelectedUser(currentSeat.userId);
  }, [mode, currentSeat]);

  useEffect(() => {
    if (mode !== "create" && isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [license, isLoading, mode]);

  useEffect(() => {
    if (mode !== "create" && licenseId && license) {
      setFormData({
        kind: getLicenseKind(license.kind),
        projectId: license.projectId || "",
        organizationId: license.organizationId || 0,
        startsAt: license.startsAt ? new Date(license.startsAt) : null,
        expiresAt: license.expiresAt ? new Date(license.expiresAt) : null,
        maxActiveUsers: license.maxActiveUsers ?? null,
        comments: license.comments || "",
        count: 1,
      });
      setErrors(initialErrors);
      setComment(license.comments || "");
    } else {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setComment("");
    }
  }, [licenseId, mode, license]);

  useEffect(() => {
    if (isMobile === null) return;

    if (!isMobile) {
      if (companyId) navigate(`/comapnies/${companyId}`, { replace: true });
      if (userId) navigate(`/users/${userId}`, { replace: true });
      if (projectId) navigate(`/projects/${projectId}`, { replace: true });
      else navigate("/licenses", { replace: true });
    }
  }, [isMobile, navigate]);

  useEffect(() => {
    setIsAddUser(false);
    setSelectedUser(null);
  }, [licenseId, mode]);

  useEffect(() => {
    if (mode === "create") {
      if (companyId)
        setFormData((prev) => ({ ...prev, organizationId: Number(companyId) }));
      if (projectId) setFormData((prev) => ({ ...prev, projectId: projectId }));
      if (company)
        setFormData((prev) => ({
          ...prev,
          organizationId: Number(company.id),
        }));
    }
  }, [mode, companyId, projectId, company]);

  // Бессрочная лицензия не имеет даты окончания
  useEffect(() => {
    if (isPerpetual && formData.expiresAt) {
      setFormData((prev) => ({ ...prev, expiresAt: null }));
      setErrors((prev) => ({ ...prev, expiresAt: false }));
    }
  }, [isPerpetual, formData.expiresAt]);

  // Сессионная лицензия создаётся всегда в одном экземпляре
  useEffect(() => {
    if (isSession && formData.count !== 1) {
      setFormData((prev) => ({ ...prev, count: 1 }));
      setErrors((prev) => ({ ...prev, count: false }));
    }
  }, [isSession, formData.count]);

  useEffect(() => {
    setIsAvailableUsers(addUserOpt.length > 0);
  }, [addUserOpt]);

  const handleFieldChange = (field: keyof CreateLicenseData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    if (mode !== "create" && mode !== "edit") return true;

    const fields: (keyof CreateLicenseData)[] =
      mode === "create"
        ? [
            "count",
            "kind",
            "projectId",
            "organizationId",
            "startsAt",
            "expiresAt",
            "maxActiveUsers",
          ]
        : ["startsAt", "expiresAt"];

    const newErrors = { ...initialErrors };
    fields.forEach((field) => {
      newErrors[field] = !validateLicenseField(field, formData[field], formData);
    });

    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateLicenseData) => {
    const isValid = validateLicenseField(field, formData[field], formData);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (!validateAllFields()) {
      setValidForm(false);
      return;
    }

    setValidForm(true);
    setSubmitting(true);
    let success = false;

    try {
      if (mode === "edit" && isAddUser) {
        if (!selectedUser) {
          setValidForm(false);
          return;
        }
        success = await handleAssignSeat(license!.id, selectedUser);
      } else {
        switch (mode) {
          case "create": {
            const addResult = await handlePostLicense(formData);
            success = addResult.success;
            if (success && userId && addResult.licenseIds) {
              for (const createdId of addResult.licenseIds) {
                await handleAssignSeat(createdId, userId);
              }
            }
            break;
          }
          case "edit": {
            const editData: EditLicenseData = {
              startsAt: formData.startsAt,
              expiresAt: isPerpetual ? null : formData.expiresAt,
              comments: comment,
            };
            success = await handleUpdateLicense(editData, license!.id);
            break;
          }
          case "addUser": {
            if (!selectedUser) return;
            success = await handleAssignSeat(license!.id, selectedUser);
            break;
          }
          case "editUser": {
            if (!selectedUser) return;
            if (currentSeat && currentSeat.userId === selectedUser) {
              success = true;
              break;
            }
            if (currentSeat) {
              success = await handleRemoveSeat(license!.id, currentSeat.userId);
              if (!success) break;
            }
            success = await handleAssignSeat(license!.id, selectedUser);
            break;
          }
          case "deleteUser": {
            if (!currentSeat) {
              success = false;
              break;
            }
            success = await handleRemoveSeat(license!.id, currentSeat.userId);
            break;
          }
          case "delete":
            success = await handleDeleteLicense(license!);
            break;
          case "enable":
            success = await handleActivateLicense(license!.id);
            break;
          case "disable":
            success = await handleDeactivateLicense(license!.id);
            break;
          default:
            success = false;
        }
      }

      if (success) {
        refetchData();
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else return false;
    } finally {
      setSubmitting(false);
    }
  };

  const getHeader = (type: string) => {
    switch (type) {
      case "create":
        return "Новая лицензия";
      case "edit":
        return "Редактировать лицензию";
      case "delete":
        return "Удалить лицензию";
      case "enable":
        return "Активировать лицензию";
      case "disable":
        return "Деактивировать лицензию";
      case "addUser":
        return "Добавить пользователя";
      case "editUser":
        return "Редактировать пользователя";
      case "deleteUser":
        return "Удалить пользователя";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const getButtonLabel = (type: string) => {
    switch (type) {
      case "create":
        return "Создать";
      case "edit":
        return "Сохранить";
      case "delete":
        return "Удалить";
      case "enable":
        return "Активировать";
      case "disable":
        return "Деактивировать";
      case "addUser":
        return "Добавить";
      case "editUser":
        return "Сохранить";
      case "deleteUser":
        return "Удалить";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const usersDropdownForm = (
    <>
      {isAvailableUsers ? (
        <CustomDropdown
          value={selectedUser}
          onChange={(val) => setSelectedUser(val)}
          options={addUserOpt}
          placeholder="Пользователь"
          icon={<IconUser size={20} />}
          filter
        />
      ) : (
        <BodyText>Нет пользователей в этой компании</BodyText>
      )}
    </>
  );

  const formContent = (
    <Column>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {mode === "create" ? (
        <>
          {!isSession && (
            <CustomInputNumberIncrement
              value={formData.count}
              setValue={(val) => handleFieldChange("count", val)}
              onBlur={() => handleBlur("count")}
              placeholder="Количество лицензий"
              invalid={errors.count}
            />
          )}
          <CustomDropdown
            value={formData.kind}
            onChange={(val) => handleFieldChange("kind", val)}
            onBlur={() => handleBlur("kind")}
            options={LICENSE_KIND_LABELS}
            placeholder="Тип лицензии"
            icon={<IconDocument size={20} />}
            invalid={errors.kind}
          />
          {projectId || project ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.projectId}
              onChange={(val) => handleFieldChange("projectId", val)}
              onBlur={() => handleBlur("projectId")}
              options={projectsOpt}
              placeholder="Проект"
              icon={<IconCase size={20} />}
              invalid={errors.projectId}
              filter
            />
          )}
          {companyId || company ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.organizationId}
              onChange={(val) => handleFieldChange("organizationId", val)}
              onBlur={() => handleBlur("organizationId")}
              options={companiesOpt}
              placeholder="Компания"
              icon={<IconCompany size={20} />}
              invalid={errors.organizationId}
              filter
            />
          )}
          {isSession && (
            <CustomInputNumberIncrement
              value={formData.maxActiveUsers}
              setValue={(val) => handleFieldChange("maxActiveUsers", val)}
              onBlur={() => handleBlur("maxActiveUsers")}
              placeholder="Максимум активных пользователей"
              invalid={errors.maxActiveUsers}
            />
          )}
          <Row>
            <CustomCalendar
              key="start-date"
              value={formData.startsAt}
              setValue={(date) => handleFieldChange("startsAt", date)}
              onBlur={() => handleBlur("startsAt")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startsAt}
            />
            {!isPerpetual && (
              <>
                <RowText>—</RowText>
                <CustomCalendar
                  key="expiration-date"
                  value={formData.expiresAt}
                  setValue={(date) => handleFieldChange("expiresAt", date)}
                  onBlur={() => handleBlur("expiresAt")}
                  placeholder="Дата окончания"
                  icon={<IconCalendar size={20} />}
                  invalid={errors.expiresAt}
                />
              </>
            )}
          </Row>
          {isPerpetual && <BodyText>Лицензия бессрочная</BodyText>}
          <InputTextarea
            autoResize
            value={formData.comments}
            onChange={(e) => handleFieldChange("comments", e.target.value)}
            rows={3}
            cols={30}
            placeholder="Комментарий"
          />
        </>
      ) : license ? (
        <>
          <SmallColumn>
            <StyledIconLine
              icon={<IconCaseFilled size={20} />}
              text={license?.projectName ?? "Нет данных"}
            />
            <StyledIconLine
              icon={<IconCompanyFilled size={20} />}
              text={`${license?.organizationName ?? "Нет данных"}`}
            />
            {currentSeat && (
              <StyledIconLine
                icon={<IconUserFilled size={20} />}
                text={seatName(currentSeat)}
              />
            )}
          </SmallColumn>
          <Row>
            <CustomCalendar
              key="start-date-license"
              value={formData.startsAt}
              setValue={(date) => handleFieldChange("startsAt", date)}
              onBlur={() => handleBlur("startsAt")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startsAt}
            />
            {!isPerpetual ? (
              <>
                <RowText>—</RowText>
                <CustomCalendar
                  key="expiration-date-license"
                  value={formData.expiresAt}
                  setValue={(date) => handleFieldChange("expiresAt", date)}
                  onBlur={() => handleBlur("expiresAt")}
                  placeholder="Дата окончания"
                  icon={<IconCalendar size={20} />}
                  invalid={errors.expiresAt}
                />
              </>
            ) : (
              <RowText>Бессрочная</RowText>
            )}
          </Row>
          <InputTextarea
            autoResize
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            cols={30}
            placeholder="Комментарий"
          />
          {currentSeat ? (
            <></>
          ) : (
            <>
              <Button
                outlined
                severity="secondary"
                onClick={() => setIsAddUser(true)}
                visible={!isAddUser}
              >
                <IconPlusBlack size={20} />
                <BodyText style={{ marginLeft: "5px" }}>
                  Добавить пользователя
                </BodyText>
              </Button>
              {isAddUser ? usersDropdownForm : <></>}
            </>
          )}
        </>
      ) : (
        <BodyText>Загрузка...</BodyText>
      )}
    </Column>
  );

  const addUserForm = (
    <Column>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={license?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${license?.organizationName ?? "Нет данных"}`}
      />
      <StyledIconLine
        icon={<IconDocFilled size={20} />}
        text={
          license?.kind
            ? `${getLicenseKindLabel(license?.kind)} лицензия`
            : "Нет данных"
        }
      />
      {usersDropdownForm}
    </Column>
  );

  const infoForm = (
    <SmallColumn>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={license?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${license?.organizationName ?? "Нет данных"}`}
      />
      {(mode === "deleteUser" || currentSeat) && (
        <StyledIconLine
          icon={<IconUserFilled size={20} />}
          text={seatName(currentSeat)}
        />
      )}
      <StyledIconLine
        icon={<IconDocFilled size={20} />}
        text={
          license?.kind
            ? `${getLicenseKindLabel(license?.kind)} лицензия`
            : "Нет данных"
        }
      />
      <StyledIconLine
        icon={<IconCalendarCheckFilled size={20} />}
        text={`Дата начала: ${
          license?.startsAt
            ? formatDate(license.startsAt.toString())
            : "Нет данных"
        }`}
      />
      <StyledIconLine
        icon={<IconCalendarXFilled size={20} />}
        text={`Дата завершения: ${
          license?.expiresAt
            ? formatDate(license.expiresAt.toString())
            : "Бессрочно"
        }`}
      />
    </SmallColumn>
  );

  const deleteUserForm = (
    <Column>
      <DangerText>
        Вы уверены, что хотите удалить пользователя из лицензии?
      </DangerText>
      {infoForm}
    </Column>
  );

  const deleteForm = (
    <Column>
      <DangerText>Вы уверены, что хотите удалить данную лицензию?</DangerText>
      {infoForm}
    </Column>
  );

  const enableForm = (
    <Column>
      <BodyText>Активировать данную лицензию?</BodyText>
      {infoForm}
    </Column>
  );

  const disableForm = (
    <Column>
      <DangerText>
        Вы уверены, что хотите деактивировать данную лицензию?
      </DangerText>
      {infoForm}
    </Column>
  );

  const getContent = (type: string) => {
    switch (type) {
      case "create":
        return formContent;
      case "edit":
        return formContent;
      case "delete":
        return deleteForm;
      case "enable":
        return enableForm;
      case "disable":
        return disableForm;
      case "addUser":
        return addUserForm;
      case "editUser":
        return addUserForm;
      case "deleteUser":
        return deleteUserForm;
      default:
        return <></>;
    }
  };

  if (loading)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <H2>{getHeader(mode)}</H2>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {getContent(mode)}
      <Flex>
        <GoBackButton>
          <Button outlined onClick={() => navigate(-1)}>
            <ButtonLabel>
              <IconArrowLeft size={16} />
              Назад
            </ButtonLabel>
          </Button>
        </GoBackButton>
        <ButtonContainer>
          <Button
            label={getButtonLabel(mode)}
            onClick={handleSubmit}
            loading={submitting}
          />
        </ButtonContainer>
      </Flex>
    </Container>
  );
};

export default LicenseFormPage;
